import type { Editor } from '@tiptap/react';
import { isEditorUsable } from './textInlineMarks';

export type ListKind = 'bullet' | 'ordered' | 'none';

const KEEP_TAGS = /<\/?(strong|b|em|i|u|span)(\s[^>]*)?>/gi;

export function detectListKind(html?: string): ListKind {
  const value = String(html || '');
  if (/<ol[\s>]/i.test(value)) return 'ordered';
  if (/<ul[\s>]/i.test(value)) return 'bullet';
  return 'none';
}

function stripLeadingBullet(item: string): string {
  return item
    .replace(/^&bull;|^&#8226;|^&#x2022;/i, '')
    .replace(/^[•●◦·▪▸►]\s*/, '')
    .trim();
}

function listItemsFromHtml(html: string): string[] {
  const source = String(html || '');
  const lis = source.match(/<li\b[^>]*>[\s\S]*?<\/li>/gi);
  if (lis?.length) {
    return lis
      .map((item) => stripLeadingBullet(item.replace(/<\/?li\b[^>]*>/gi, '').trim()))
      .filter(Boolean);
  }
  return source
    .replace(/<\/(p|h[1-6]|div)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .split(/\n+/)
    .map((item) => item.replace(/<(?!\/?(strong|b|em|i|u|span)\b)[^>]+>/gi, ''))
    .map((item) => stripLeadingBullet(item.trim()))
    .filter(Boolean);
}

export function applyListHtml(html: string, kind: ListKind): string {
  const items = listItemsFromHtml(html);
  if (!items.length) return html;
  if (kind === 'none') return items.map((item) => `<p>${item}</p>`).join('');
  const tag = kind === 'ordered' ? 'ol' : 'ul';
  return `<${tag}>${items.map((item) => `<li>${item}</li>`).join('')}</${tag}>`;
}

export function applyLiveList(editor: Editor, kind: ListKind): boolean {
  if (!isEditorUsable(editor)) return false;
  if (kind === 'bullet') return editor.chain().focus().toggleBulletList().run();
  if (kind === 'ordered') return editor.chain().focus().toggleOrderedList().run();
  if (editor.isActive('bulletList')) return editor.chain().focus().toggleBulletList().run();
  if (editor.isActive('orderedList')) return editor.chain().focus().toggleOrderedList().run();
  return false;
}

export function preserveListMarks(html: string): boolean {
  return KEEP_TAGS.test(html);
}
